/**
 * Upcoming Leaves Hook
 *
 * Supplies the manager's team members' approved leaves that fall within the
 * next N days (default 30), starting tomorrow, for the "Staff on leave"
 * metric + drill-down modal on the Manager Dashboard. Today itself is
 * excluded — a leave in progress today belongs to "On leave today"
 * (useOnLeaveToday), not here.
 *
 * Data flow (source: `/data/LeaveRequestsV2`):
 *   1. Team roster comes from useTeamMembers (EssWorkerManagerRelations).
 *   2. Per member, LeaveRequestsV2 is queried filtered to LeaveDate within the
 *      window — a scoped query, not the whole history.
 *   3. Day-lines are grouped by RequestId into one leave per request, giving a
 *      start/end period and day count.
 *   4. Leave type Description comes from the LeaveTypes lookup.
 *   5. The authoritative status is read from EssLeaveRequestHeaders (LeaveStatus)
 *      per RequestId — only Approved leaves count. Mirrors LeaveContext /
 *      useTeamLeaveRequests, the proven path for these entities.
 *
 * Each entry is one upcoming leave (a member may appear more than once if they
 * have several), mapped into a card-friendly shape:
 *   { id, personnelNumber, name, role, status, leaveTypeName,
 *     startDate, endDate, days, requestId, dataAreaId }
 * `id` is the RequestId so list keys stay unique; `personnelNumber` still lets
 * a row open the shared TeamMemberDetailModal.
 *
 * Returns:
 *   - upcomingLeaves: Array<mapped leave>
 *   - count: number   — number of upcoming approved leaves
 *   - isLoading: boolean
 *   - error: Error | null
 */
import { useQuery } from "react-query";
import { leaveService } from "../../api/services";
import useTeamMembers from "./useTeamMembers";

const WINDOW_DAYS = 30;
const APPROVED_STATUSES = new Set(["Approved", "approved"]);

/**
 * Inclusive [from, to] ISO window: start of tomorrow → end of day +days.
 * Excludes today so a leave already in progress is only counted under
 * "On leave today" (useOnLeaveToday), never double-counted here too.
 *
 * This is sent to the server as a courtesy pre-filter only — it is a
 * millisecond-exact UTC boundary, so it must not be trusted as the sole
 * guard against today's leave leaking in (the query function below
 * re-checks each result's calendar date against `tomorrowKey`).
 */
const windowBoundsIso = (days, referenceDate) => {
  const from = new Date(referenceDate);
  from.setDate(from.getDate() + 1);
  from.setHours(0, 0, 0, 0);
  const to = new Date(referenceDate);
  to.setDate(to.getDate() + days);
  to.setHours(23, 59, 59, 999);
  return { fromIso: from.toISOString(), toIso: to.toISOString() };
};

/** Local YYYY-MM-DD key for a date, avoiding UTC shift. */
const toLocalKey = (date) => {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/**
 * A leave date is a calendar date, not a point in time — D365 returns
 * LeaveDate as a UTC timestamp (seen as both T00:00:00Z and T12:00:00Z
 * depending on the record), so reading it with local getters can roll the
 * calendar date across a day boundary depending on the browser's timezone
 * offset (see useTeamBirthdays.js for the same class of bug). Always read
 * the calendar date in UTC instead of trusting the server's ge/le filter
 * alone, which is a zero-margin millisecond boundary.
 */
const toUtcDateKey = (dateStr) => {
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return null;
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, "0");
  const d = String(date.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

/** Group an employee's day-lines into one leave per RequestId. */
const groupByRequestId = (lines) => {
  const grouped = {};
  lines.forEach((line) => {
    const requestId = line.RequestId;
    if (!requestId) return;
    if (!grouped[requestId]) {
      grouped[requestId] = {
        requestId,
        dataAreaId: line.dataAreaId || "usmf",
        leaveType: line.LeaveType,
        status: line.Status,
        dates: [],
        totalAmount: 0,
      };
    }
    if (line.LeaveDate) grouped[requestId].dates.push(line.LeaveDate);
    grouped[requestId].totalAmount += line.Amount || 0;
  });
  return Object.values(grouped).map((g) => {
    g.dates.sort((a, b) => new Date(a) - new Date(b));
    return {
      ...g,
      startDate: g.dates[0] || null,
      endDate: g.dates[g.dates.length - 1] || null,
      // Sum of each day's Amount, not the number of day-records — a leave
      // line can be a half-day (Amount: 0.5), so dates.length overcounts
      // whenever the request includes any partial day.
      days: g.totalAmount,
    };
  });
};

export default function useUpcomingLeaves(
  days = WINDOW_DAYS,
  referenceDate = new Date()
) {
  const {
    members,
    isLoading: membersLoading,
    error: membersError,
  } = useTeamMembers();

  const personnelKey = members.map((m) => m.personnelNumber).join(",");
  const dayKey = toLocalKey(referenceDate);

  const {
    data: upcomingLeaves,
    isLoading: leavesLoading,
    error: leavesError,
  } = useQuery(
    ["upcomingLeaves", days, dayKey, personnelKey],
    async () => {
      const { fromIso, toIso } = windowBoundsIso(days, referenceDate);

      // Calendar-date cutoff for the client-side re-check below: the local
      // calendar day after referenceDate (i.e. "tomorrow" as the manager
      // sees it), as a plain YYYY-MM-DD key.
      const cutoff = new Date(referenceDate);
      cutoff.setDate(cutoff.getDate() + 1);
      const tomorrowKey = toLocalKey(cutoff);

      // LeaveType -> Description lookup (best-effort; falls back to raw code).
      const descMap = {};
      try {
        const leaveTypesResponse = await leaveService.getLeaveTypes();
        (leaveTypesResponse.data?.value || []).forEach((lt) => {
          descMap[lt.Type] = lt.Description;
        });
      } catch {
        // optional
      }

      const perMember = await Promise.all(
        members.map(async (member) => {
          try {
            const response =
              await leaveService.getLeaveRequestsForEmployeeInRange(
                member.personnelNumber,
                fromIso,
                toIso
              );
            const grouped = groupByRequestId(response.data?.value || []);
            if (grouped.length === 0) return [];

            // Defensive re-check against the intended window: the OData
            // ge/le filter above is a millisecond-exact UTC boundary with no
            // margin, so a server-side rounding/precision quirk could return
            // a leave that starts today. Trust only the leave's own calendar
            // date (UTC, matching how D365 stores LeaveDate) here, not the
            // filter having done its job.
            const upcoming = grouped.filter(
              (req) => req.startDate && toUtcDateKey(req.startDate) >= tomorrowKey
            );
            if (upcoming.length === 0) return [];

            // Resolve authoritative header status; keep only approved leaves.
            const resolved = await Promise.all(
              upcoming.map(async (req) => {
                let status = req.status;
                try {
                  const headerRes =
                    await leaveService.getEssLeaveRequestHeaderById(
                      req.requestId,
                      req.dataAreaId
                    );
                  status = headerRes.data?.LeaveStatus || status;
                } catch {
                  // fall back to the LeaveRequestsV2 Status
                }
                if (!APPROVED_STATUSES.has(status)) return null;

                const leaveTypeName = descMap[req.leaveType] || req.leaveType;
                return {
                  id: req.requestId,
                  personnelNumber: member.personnelNumber,
                  name: member.name,
                  role: leaveTypeName || "On Leave",
                  status: "Upcoming",
                  leaveTypeName,
                  startDate: req.startDate,
                  endDate: req.endDate,
                  days: req.days,
                  requestId: req.requestId,
                  dataAreaId: req.dataAreaId,
                };
              })
            );

            return resolved.filter(Boolean);
          } catch (err) {
            console.error(
              `❌ Failed to fetch upcoming leave for ${member.personnelNumber}:`,
              err
            );
            return [];
          }
        })
      );

      // Flatten and sort by soonest start date.
      return perMember
        .flat()
        .sort((a, b) => new Date(a.startDate) - new Date(b.startDate));
    },
    {
      enabled: members.length > 0,
      retry: 2,
      staleTime: 5 * 60 * 1000,
    }
  );

  return {
    upcomingLeaves: upcomingLeaves ?? [],
    count: upcomingLeaves?.length ?? 0,
    isLoading: membersLoading || (members.length > 0 && leavesLoading),
    error: membersError || leavesError || null,
  };
}
